import { useCallback, useEffect, useRef, useState } from 'react';

import { V1Error } from '@/papermill/api';
import {
  applyWords,
  createLiveJob,
  describeLiveError,
  encodeFrame,
  endLiveSession,
  FINAL_CLOSE_CODES,
  FRAME_MS,
  makeResampler,
  mintStreamTicket,
  NO_WORDS,
  type LiveEvent,
  type LiveWords,
} from '@/papermill/live';

export type LivePhase =
  | 'idle'
  | 'starting'
  | 'connecting'
  | 'recording'
  | 'reconnecting'
  | 'finishing'
  | 'ended'
  | 'failed';

export interface LiveSession {
  phase: LivePhase;
  jobId: string | null;
  words: LiveWords;
  /** Loudness of the latest frame, 0–1, smoothed. */
  level: number;
  /** Audio captured so far, by the frames' own clock. */
  elapsedMs: number;
  /** How far the words run behind the voice: median of recent frames, send to first text. */
  lagMs: number | null;
  secondsRemaining: number | null;
  notice: { code: string; message: string } | null;
  error: string | null;
  start: (locale: string) => Promise<void>;
  finish: () => void;
  reset: () => void;
}

/** Runs a task with the secret the session is streamed on, e.g. the Demo key's. */
export type WithSecret = <T>(task: (secret: string) => Promise<T>) => Promise<T>;

const WORKLET = `class Tap extends AudioWorkletProcessor {
  process(inputs) { const ch = inputs[0] && inputs[0][0]; if (ch) this.port.postMessage(ch.slice(0)); return true; }
}
registerProcessor('tap', Tap);`;

const RECONNECT_ATTEMPTS = 5;
const FLUSH_MS = 5_000;
const ENDED_TIMEOUT_MS = 15_000;

interface Frame {
  seq: number;
  data: ArrayBuffer;
}

interface Wire {
  jobId: string;
  ws: WebSocket | null;
  ready: boolean;
  /** Set on the first `ready`; before it, audio only drives the meter. */
  streaming: boolean;
  sentAt: Map<number, number>;
  lags: number[];
  pending: Frame[];
  seq: number;
  ending: boolean;
  ended: boolean;
  attempts: number;
  lastError: { code?: string; message: string } | null;
  stream: MediaStream | null;
  context: AudioContext | null;
  endedTimer: ReturnType<typeof setTimeout> | null;
}

function micError(error: unknown): string {
  const name = error instanceof DOMException ? error.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Microphone access is blocked. Allow it for this site in your browser, then try again.';
  }
  if (name === 'NotFoundError') {
    return 'No microphone was found. Plug one in, or try another device.';
  }
  return 'The microphone could not be opened.';
}

/**
 * A live session end to end: the microphone, Arcade's live job, its socket
 * (resent from `resume_from` after a drop) and the words as they arrive.
 * `onEnded` fires once the session has become an ordinary recording job.
 */
export function useLiveSession(
  withSecret: WithSecret,
  onEnded: (jobId: string) => void,
): LiveSession {
  const [phase, setPhase] = useState<LivePhase>('idle');
  const [jobId, setJobId] = useState<string | null>(null);
  const [words, setWords] = useState<LiveWords>(NO_WORDS);
  const [level, setLevel] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [lagMs, setLagMs] = useState<number | null>(null);
  const [secondsRemaining, setSecondsRemaining] = useState<number | null>(null);
  const [notice, setNotice] = useState<LiveSession['notice']>(null);
  const [error, setError] = useState<string | null>(null);
  const wire = useRef<Wire | null>(null);
  const onEndedRef = useRef(onEnded);
  const withSecretRef = useRef(withSecret);
  const reconnectRef = useRef<(url: string) => void>(() => undefined);

  useEffect(() => {
    onEndedRef.current = onEnded;
    withSecretRef.current = withSecret;
  });

  const stopCapture = useCallback(async () => {
    const current = wire.current;
    if (!current) {
      return;
    }
    current.stream?.getTracks().forEach((track) => track.stop());
    await current.context?.close().catch(() => undefined);
    current.stream = null;
    current.context = null;
    setLevel(0);
  }, []);

  const conclude = useCallback(() => {
    const current = wire.current;
    if (!current || current.ended) {
      return;
    }
    current.ended = true;
    if (current.endedTimer) {
      clearTimeout(current.endedTimer);
    }
    void stopCapture();
    current.ws?.close();
    setPhase('ended');
    onEndedRef.current(current.jobId);
  }, [stopCapture]);

  const fail = useCallback(
    (message: string) => {
      const current = wire.current;
      if (current) {
        current.ended = true;
        current.ws?.close();
      }
      void stopCapture();
      setError(message);
      setPhase('failed');
    },
    [stopCapture],
  );

  const endOverRest = useCallback(async () => {
    const current = wire.current;
    if (!current || current.ended) {
      return;
    }
    try {
      await withSecretRef.current((secret) => endLiveSession(current.jobId, secret));
    } catch {
      // The session may already be over; the job page reports what happened either way.
    }
    conclude();
  }, [conclude]);

  const send = useCallback((frame: Frame) => {
    const current = wire.current;
    const ws = current?.ws;
    if (current && ws && ws.readyState === WebSocket.OPEN && current.ready) {
      ws.send(frame.data);
      if (!current.sentAt.has(frame.seq)) {
        current.sentAt.set(frame.seq, performance.now());
      }
    }
  }, []);

  const measure = useCallback((endMs: number) => {
    const current = wire.current;
    const seq = Math.floor((endMs - 1) / FRAME_MS);
    const sent = current?.sentAt.get(seq);
    if (!current || sent === undefined || endMs <= 0) {
      return;
    }
    current.lags = [...current.lags.slice(-9), performance.now() - sent];
    for (const key of current.sentAt.keys()) {
      if (key < seq - 200) {
        current.sentAt.delete(key);
      }
    }
    const sorted = [...current.lags].sort((a, b) => a - b);
    setLagMs(sorted[Math.floor(sorted.length / 2)]);
  }, []);

  const connect = useCallback(
    (url: string) => {
      const current = wire.current;
      if (!current) {
        return;
      }
      const ws = new WebSocket(url);
      ws.binaryType = 'arraybuffer';
      current.ws = ws;
      current.ready = false;

      ws.onmessage = (message) => {
        if (typeof message.data !== 'string') {
          return;
        }
        const event = JSON.parse(message.data) as LiveEvent;
        if (typeof event.seconds_remaining === 'number') {
          setSecondsRemaining(event.seconds_remaining);
        }
        switch (event.type) {
          case 'waiting':
            setPhase('connecting');
            break;
          case 'ready': {
            current.ready = true;
            if (!current.streaming) {
              navigator.vibrate?.(40);
            }
            current.streaming = true;
            current.attempts = 0;
            const from = Math.min(...event.tracks.map((track) => track.resume_from), current.seq);
            current.pending = current.pending.filter((frame) => frame.seq >= from);
            current.pending.forEach(send);
            setPhase(current.ending ? 'finishing' : 'recording');
            break;
          }
          case 'ack':
            current.pending = current.pending.filter((frame) => frame.seq > event.seq);
            break;
          case 'partial':
          case 'final':
            measure(event.end_ms);
            setWords((previous) => applyWords(previous, event));
            break;
          case 'notice':
            setNotice({ code: event.code, message: event.message });
            if (event.code === 'credits_exhausted') {
              current.ending = true;
              setPhase('finishing');
              void stopCapture();
            }
            break;
          case 'error':
            current.lastError = { code: event.code, message: event.message };
            break;
          case 'ended':
            conclude();
            break;
        }
      };

      ws.onclose = (close) => {
        if (current.ws !== ws || current.ended) {
          return;
        }
        current.ready = false;
        if (FINAL_CLOSE_CODES.has(close.code)) {
          const last = current.lastError;
          fail(describeLiveError(last?.code, last?.message ?? ''));
          return;
        }
        if (current.ending) {
          void endOverRest();
          return;
        }
        if (current.attempts >= RECONNECT_ATTEMPTS) {
          void endOverRest();
          return;
        }
        current.attempts += 1;
        setPhase('reconnecting');
        setTimeout(() => {
          if (current.ended || current.ending) {
            return;
          }
          withSecretRef
            .current((secret) => mintStreamTicket(current.jobId, secret))
            .then((ticket) => reconnectRef.current(ticket.url))
            .catch(() => void endOverRest());
        }, 1000 * current.attempts);
      };
    },
    [conclude, endOverRest, fail, measure, send, stopCapture],
  );

  useEffect(() => {
    reconnectRef.current = connect;
  }, [connect]);

  const start = useCallback(
    async (locale: string) => {
      setError(null);
      setNotice(null);
      setWords(NO_WORDS);
      setElapsedMs(0);
      setLagMs(null);
      setSecondsRemaining(null);
      setPhase('starting');
      const context = new AudioContext({ latencyHint: 'interactive' });
      const module = URL.createObjectURL(new Blob([WORKLET], { type: 'text/javascript' }));
      const [mic, job, worklet] = await Promise.allSettled([
        navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            echoCancellation: false,
            noiseSuppression: true,
            autoGainControl: true,
          },
        }),
        withSecretRef.current((secret) => createLiveJob(secret, locale)),
        context.audioWorklet.addModule(module),
      ]);
      URL.revokeObjectURL(module);
      if (mic.status === 'rejected' || job.status === 'rejected' || worklet.status === 'rejected') {
        void context.close().catch(() => undefined);
        if (mic.status === 'fulfilled') {
          mic.value.getTracks().forEach((track) => track.stop());
        }
        if (job.status === 'fulfilled') {
          const id = job.value.id;
          void withSecretRef.current((secret) => endLiveSession(id, secret)).catch(() => undefined);
        }
        const failure =
          mic.status === 'rejected' ? mic.reason : job.status === 'rejected' ? job.reason : null;
        setError(
          mic.status === 'rejected'
            ? micError(mic.reason)
            : failure instanceof V1Error
              ? describeLiveError(failure.code, failure.message)
              : failure
                ? 'The live session could not be started.'
                : 'This browser cannot record live audio. Try a recent Chrome, Edge, Firefox or Safari.',
        );
        setPhase('failed');
        return;
      }
      const stream = mic.value;
      const created = job.value;
      wire.current = {
        jobId: created.id,
        ws: null,
        ready: false,
        streaming: false,
        sentAt: new Map(),
        lags: [],
        pending: [],
        seq: 0,
        ending: false,
        ended: false,
        attempts: 0,
        lastError: null,
        stream,
        context,
        endedTimer: null,
      };
      setJobId(created.id);
      setPhase('connecting');
      connect(created.stream.url);

      const source = context.createMediaStreamSource(stream);
      const tap = new AudioWorkletNode(context, 'tap');
      let resample = makeResampler(context.sampleRate);
      let smoothed = 0;
      let shownAt = 0;
      const meter = (samples: Float32Array) => {
        let sum = 0;
        for (let i = 0; i < samples.length; i++) {
          sum += samples[i] * samples[i];
        }
        smoothed = Math.max(Math.min(1, Math.sqrt(sum / samples.length) * 4), smoothed * 0.8);
        const now = performance.now();
        if (now - shownAt >= FRAME_MS) {
          shownAt = now;
          setLevel(smoothed);
        }
      };
      tap.port.onmessage = (message: MessageEvent<Float32Array>) => {
        const current = wire.current;
        if (!current || current.ending) {
          return;
        }
        if (!current.streaming) {
          resample = makeResampler(context.sampleRate);
          meter(message.data);
          return;
        }
        for (const samples of resample(message.data)) {
          const frame = { seq: current.seq, data: encodeFrame(current.seq, samples) };
          current.seq += 1;
          current.pending.push(frame);
          send(frame);
          meter(samples);
          setElapsedMs(current.seq * FRAME_MS);
        }
      };
      source.connect(tap);
    },
    [connect, send],
  );

  const finish = useCallback(() => {
    const current = wire.current;
    if (!current || current.ending || current.ended) {
      return;
    }
    current.ending = true;
    setPhase('finishing');
    void stopCapture();
    current.endedTimer = setTimeout(() => void endOverRest(), ENDED_TIMEOUT_MS);
    const deadline = Date.now() + FLUSH_MS;
    const flush = () => {
      const ws = current.ws;
      if (current.ended) {
        return;
      }
      if (ws && ws.readyState === WebSocket.OPEN && current.ready) {
        ws.send(JSON.stringify({ type: 'end' }));
        return;
      }
      if (Date.now() < deadline) {
        setTimeout(flush, 200);
        return;
      }
      void endOverRest();
    };
    flush();
  }, [endOverRest, stopCapture]);

  const reset = useCallback(() => {
    wire.current = null;
    setPhase('idle');
    setJobId(null);
    setWords(NO_WORDS);
    setError(null);
    setNotice(null);
    setElapsedMs(0);
    setLagMs(null);
    setSecondsRemaining(null);
  }, []);

  useEffect(
    () => () => {
      const current = wire.current;
      if (current && !current.ended) {
        current.ending = true;
        if (current.ws?.readyState === WebSocket.OPEN) {
          current.ws.send(JSON.stringify({ type: 'end' }));
        }
        current.stream?.getTracks().forEach((track) => track.stop());
        void current.context?.close().catch(() => undefined);
      }
    },
    [],
  );

  return {
    phase,
    jobId,
    words,
    level,
    elapsedMs,
    lagMs,
    secondsRemaining,
    notice,
    error,
    start,
    finish,
    reset,
  };
}
